import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Telescope, PlusCircle, Home } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import RentalCard from "@/components/RentalCard";
import RentalDetailModal from "@/components/RentalDetailModal";
import { fetchSharedList, addToSharedList, updateSharedRental } from "@/lib/api";
import ManualAddDialog from "@/components/ManualAddDialog";

export default function SharedListPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);   // { owner_name, rentals, total }
  const [selected, setSelected] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      const result = await fetchSharedList(token);
      setData(result);
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
      else toast.error("Could not load shared list");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdded = () => {
    load();
    setManualOpen(false);
  };

  const handleUpdate = async (rentalId, changes) => {
    const updated = await updateSharedRental(token, rentalId, changes);
    setData((prev) => ({
      ...prev,
      rentals: prev.rentals.map((r) => (r.id === rentalId ? updated : r)),
    }));
    setSelected(updated);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-stone-300 border-t-[#4A5D4E] animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center text-stone-400 mb-4">
            <Home size={28} strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">List not found</h1>
          <p className="text-stone-500 mt-2">
            This link may have been disabled by its owner.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#F9F8F6]/75 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#4A5D4E] flex items-center justify-center text-[#F9F8F6] -rotate-6">
              <Telescope size={20} strokeWidth={1.6} />
            </div>
            <div className="leading-none">
              <div className="font-heading text-2xl font-medium tracking-tight">
                LeaseDeck
              </div>
              <div className="overline mt-1">
                {data?.owner_name ? `${data.owner_name}'s shortlist` : "Shared shortlist"}
              </div>
            </div>
          </div>

          <Button
            onClick={() => setManualOpen(true)}
            className="inline-flex items-center gap-1.5 bg-[#4A5D4E] hover:bg-[#3D4D40] text-[#F9F8F6] rounded-full px-4"
            size="sm"
          >
            <PlusCircle size={15} strokeWidth={1.8} />
            <span className="hidden sm:inline">Add rental</span>
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="mb-6">
          <h2 className="font-heading text-3xl font-medium tracking-tight">
            {data?.total ?? 0} {data?.total === 1 ? "rental" : "rentals"}
          </h2>
          <p className="text-stone-500 mt-1 text-sm">
            You're viewing a shared list. Add a rental using the button above.
          </p>
        </div>

        {data?.rentals?.length === 0 ? (
          <div className="bg-white/60 border border-stone-200 rounded-3xl p-12 text-center">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-[#4A5D4E]/10 flex items-center justify-center text-[#4A5D4E] mb-4">
              <Home size={28} strokeWidth={1.5} />
            </div>
            <p className="font-heading text-2xl font-medium tracking-tight">No rentals yet</p>
            <p className="text-stone-500 mt-2">Be the first to add one!</p>
            <Button
              onClick={() => setManualOpen(true)}
              className="mt-5 bg-[#4A5D4E] hover:bg-[#3D4D40] text-[#F9F8F6] rounded-full"
            >
              <PlusCircle size={15} strokeWidth={1.8} className="mr-2" />
              Add rental
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {data.rentals.map((r, i) => (
              <RentalCard
                key={r.id}
                rental={r}
                index={i}
                onOpen={setSelected}
              />
            ))}
          </div>
        )}
      </main>

      <RentalDetailModal
        rental={selected}
        onClose={() => setSelected(null)}
        onUpdate={handleUpdate}
      />

      {/* Manual add wired to the share endpoint, not the authed one */}
      <ManualAddDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        onCreated={handleAdded}
        shareToken={token}
      />
    </div>
  );
}
